class SeatTypesService {
  constructor() {
    this.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
  }

  getAuthHeaders() {
    // Los endpoints de seat-types están marcados como @Public(), 
    // por lo que no necesitamos enviar token de autenticación
    return {
      'Content-Type': 'application/json'
    };
  }

  /**
   * Obtener todos los tipos de asientos
   */
  async getAllSeatTypes() {
    try {
      const response = await fetch(`${this.baseURL}/seat-types`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Error al obtener tipos de asientos');
      }

      return await response.json();
    } catch (error) {
      console.error('Error en getAllSeatTypes:', error);
      throw error;
    }
  }

  /**
   * Obtener tipos de asientos por evento
   */
  async getSeatTypesByEvent(eventId) {
    try {
      const response = await fetch(`${this.baseURL}/seat-types/event/${eventId}`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Error al obtener tipos de asientos del evento');
      }

      return await response.json();
    } catch (error) {
      console.error('Error en getSeatTypesByEvent:', error);
      throw error;
    }
  }

  /**
   * Obtener tipos de asientos por evento con descripciones
   */
  async getSeatTypesByEventWithDescriptions(eventId) {
    try {
      const response = await fetch(`${this.baseURL}/seat-types/event/${eventId}/with-descriptions`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Error al obtener tipos de asientos del evento');
      }

      return await response.json();
    } catch (error) {
      console.error('Error en getSeatTypesByEventWithDescriptions:', error);
      throw error;
    }
  }

  /**
   * Crear tipo de asiento
   */
  // Usa el mensaje del backend (ej. rangos de filas superpuestos) si viene en la respuesta
  async getErrorMessage(response, fallback) {
    try {
      const body = await response.json();
      const message = Array.isArray(body.message) ? body.message.join('. ') : body.message;
      return message || fallback;
    } catch {
      return fallback;
    }
  }

  async createSeatType(seatTypeData) {
    try {
      const response = await fetch(`${this.baseURL}/seat-types`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(seatTypeData),
      });

      if (!response.ok) {
        throw new Error(await this.getErrorMessage(response, 'Error al crear tipo de asiento'));
      }

      return await response.json();
    } catch (error) {
      console.error('Error en createSeatType:', error);
      throw error;
    }
  }

  /**
   * Crear tipos por defecto para un evento
   */
  async createDefaultSeatTypes(eventId) {
    try {
      const response = await fetch(`${this.baseURL}/seat-types/event/${eventId}/defaults`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Error al crear tipos por defecto');
      }

      return await response.json();
    } catch (error) {
      console.error('Error en createDefaultSeatTypes:', error);
      throw error;
    }
  }

  /**
   * Actualizar tipo de asiento
   */
  async updateSeatType(typeId, updateData) {
    try {
      const response = await fetch(`${this.baseURL}/seat-types/${typeId}`, {
        method: 'PATCH',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        throw new Error(await this.getErrorMessage(response, 'Error al actualizar tipo de asiento'));
      }

      return await response.json();
    } catch (error) {
      console.error('Error en updateSeatType:', error);
      throw error;
    }
  }

  /**
   * Eliminar tipo de asiento
   */
  async deleteSeatType(typeId) {
    try {
      const response = await fetch(`${this.baseURL}/seat-types/${typeId}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Error al eliminar tipo de asiento');
      }

      return true;
    } catch (error) {
      console.error('Error en deleteSeatType:', error);
      throw error;
    }
  }

  /**
   * Calcular precio de asiento
   */
  async calculateSeatPrice(eventId, compositeId) {
    try {
      const response = await fetch(`${this.baseURL}/seat-types/event/${eventId}/calculate-price/${compositeId}`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Error al calcular precio del asiento');
      }

      const data = await response.json();
      return data.price;
    } catch (error) {
      console.error('Error en calculateSeatPrice:', error);
      throw error;
    }
  }

  /**
   * Obtener un tipo de asiento por ID
   */
  async getSeatTypeById(typeId) {
    try {
      const response = await fetch(`${this.baseURL}/seat-types/${typeId}`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Error al obtener tipo de asiento');
      }

      return await response.json();
    } catch (error) {
      console.error('Error en getSeatTypeById:', error);
      throw error;
    }
  }
}

export const seatTypesService = new SeatTypesService(); 